/**
 * Bounded-attempt guard for token/link-based logins (ghost login, SAML
 * short-lived token exchange).
 *
 * Background (2026-08-24 prod incident): when the loginViaToken call failed
 * (that day: CORS preflight rejection, so a network-level error with no HTTP
 * response), the login flow retried forever — the failure left `?token=...`
 * in the URL, every remount re-dispatched the effect, and nothing counted
 * attempts — hammering the API from every stuck tab and pinning the browser.
 *
 * This module makes any token-login attempt:
 *  - bounded: at most TOKEN_LOGIN_MAX_ATTEMPTS requests per exact credential
 *    value, with backoff between retries, across ALL invocations (remounts,
 *    re-dispatches, StrictMode double-effects — the ledger is module-level);
 *  - single-flight: concurrent invocations for the same credential collapse
 *    into one;
 *  - terminal: once exhausted, further invocations are no-ops until a full
 *    page load, so a lingering token in the URL can never respin the loop.
 *
 * Only transient failures (no HTTP response — network/CORS/timeout — or a
 * 5xx) are retried. A 4xx means the credential itself is bad; retrying it is
 * useless and gets one attempt only.
 */

export const TOKEN_LOGIN_MAX_ATTEMPTS = 3;

// Backoff before retry N (ms). Exported as a mutable array on purpose: tests
// shorten the delays in place; runTokenLogin reads it at call time.
export const TOKEN_LOGIN_RETRY_DELAYS_MS = [1000, 2000];

// key -> { attempts, inFlight, exhausted }
const ledger = new Map();

const waitMs = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Transient = worth retrying: no HTTP response at all (network error, CORS
 * rejection, timeout) or a server-side 5xx. Anything with a 4xx response is
 * a bad credential and must not be retried.
 */
export function isTransientTokenLoginError(err) {
  if (!err) {
    return false;
  }
  const status = err?.response?.status;
  if (status === undefined || status === null) {
    return true;
  }
  return status >= 500;
}

/** Terminal-failure marker for non-error outcomes (e.g. a 200 response whose
 *  body says the token is invalid). Blocks all future attempts for this key. */
export function markTokenLoginExhausted(key) {
  const entry = ledger.get(key) ?? { attempts: 0, inFlight: false, exhausted: false };
  entry.exhausted = true;
  ledger.set(key, entry);
}

export function isTokenLoginExhausted(key) {
  return ledger.get(key)?.exhausted === true;
}

/** Test hook: wipe the module-level ledger between tests. */
export function resetTokenLoginAttempts() {
  ledger.clear();
}

/**
 * Runs `doRequest` for `key` under the bounded/single-flight rules.
 *
 * Returns exactly one of:
 *  - { status: 'ok', res }                      — request succeeded; ledger cleared
 *  - { status: 'failed', err, transient }       — attempts exhausted (or a
 *      non-retriable 4xx); key is now terminally blocked
 *  - { status: 'blocked', reason }              — 'in-flight' (another
 *      invocation is running) or 'exhausted' (already terminally failed);
 *      no request was made
 */
export async function runTokenLogin(key, doRequest, { retryDelaysMs = TOKEN_LOGIN_RETRY_DELAYS_MS } = {}) {
  const existing = ledger.get(key);
  if (existing?.exhausted) {
    return { status: 'blocked', reason: 'exhausted' };
  }
  if (existing?.inFlight) {
    return { status: 'blocked', reason: 'in-flight' };
  }

  const entry = existing ?? { attempts: 0, inFlight: false, exhausted: false };
  ledger.set(key, entry);
  entry.inFlight = true;
  try {
    while (entry.attempts < TOKEN_LOGIN_MAX_ATTEMPTS) {
      entry.attempts += 1;
      try {
        const res = await doRequest();
        // Success wipes the ledger entry so the same link can be used again
        // later in this page's lifetime (e.g. logout then re-click).
        ledger.delete(key);
        return { status: 'ok', res };
      } catch (err) {
        const transient = isTransientTokenLoginError(err);
        const attemptsLeft = entry.attempts < TOKEN_LOGIN_MAX_ATTEMPTS;
        if (!transient || !attemptsLeft) {
          entry.exhausted = true;
          return { status: 'failed', err, transient };
        }
        await waitMs(retryDelaysMs[Math.min(entry.attempts - 1, retryDelaysMs.length - 1)]);
      }
    }
    // attempts were already spent by earlier invocations
    entry.exhausted = true;
    return { status: 'blocked', reason: 'exhausted' };
  } finally {
    entry.inFlight = false;
  }
}
